import React, { useMemo, useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import {
  ShieldCheck,
  Mail,
  Server,
  ShieldCheck as ShieldCheckIcon,
  Cpu,
  HardDrive,
  Globe,
  Key,
  Lock,
  Cloud,
  Star,
  Users,
  History,
  LogOut,
  LayoutGrid,
  Plus,
  Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import ImportModal from "@/components/vault/ImportModal";

const ICON_MAP = {
  Mail,
  Server,
  ShieldCheck: ShieldCheckIcon,
  Cpu,
  HardDrive,
  Globe,
  Key,
  Lock,
  Cloud,
};

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [importOpen, setImportOpen] = useState(false);
  const [stats, setStats] = useState({ by_category: {}, total: 0, favorites: 0 });

  const fetchStats = async () => {
    try {
      const { data } = await api.get("/secrets/stats");
      setStats(data);
    } catch {}
  };

  useEffect(() => {
    fetchStats();
    const h = () => fetchStats();
    window.addEventListener("vaultops:refresh", h);
    return () => window.removeEventListener("vaultops:refresh", h);
  }, []);

  const initials = useMemo(() => {
    if (!user?.name) return "?";
    return user.name
      .split(" ")
      .map((p) => p[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-[#090d16] text-slate-100">
      {/* Sidebar */}
      <aside className="hidden lg:flex w-64 flex-shrink-0 flex-col border-r border-slate-800 bg-[#0d121f]">
        <div className="h-16 px-5 flex items-center gap-2.5 border-b border-slate-800">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">
              Vault<span className="text-emerald-400">Ops</span>
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              Team Vault
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          <NavLink
            to="/dashboard"
            end
            data-testid="nav-all"
            className={({ isActive }) =>
              `flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                  : "text-slate-300 hover:bg-slate-800/60 border border-transparent"
              }`
            }
          >
            <span className="flex items-center gap-2.5">
              <LayoutGrid className="w-4 h-4" />
              Tüm Kayıtlar
            </span>
            <Badge variant="secondary" className="bg-slate-800 text-slate-300 border-0 text-[10px] font-mono">
              {stats.total || 0}
            </Badge>
          </NavLink>

          <NavLink
            to="/dashboard/favorites"
            data-testid="nav-favorites"
            className={({ isActive }) =>
              `flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                  : "text-slate-300 hover:bg-slate-800/60 border border-transparent"
              }`
            }
          >
            <span className="flex items-center gap-2.5">
              <Star className="w-4 h-4" />
              Favoriler
            </span>
            <Badge variant="secondary" className="bg-slate-800 text-slate-300 border-0 text-[10px] font-mono">
              {stats.favorites || 0}
            </Badge>
          </NavLink>

          <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            Kategoriler
          </div>

          {CATEGORIES.map((c) => {
            const Icon = ICON_MAP[c.icon] || Lock;
            const count = stats.by_category?.[c.id] || 0;
            return (
              <NavLink
                key={c.id}
                to={`/dashboard/category/${c.id}`}
                data-testid={`category-filter-${c.id}`}
                className={({ isActive }) =>
                  `flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                      : "text-slate-300 hover:bg-slate-800/60 border border-transparent"
                  }`
                }
              >
                <span className="flex items-center gap-2.5">
                  <Icon className="w-4 h-4" />
                  {c.label}
                </span>
                <Badge variant="secondary" className="bg-slate-800 text-slate-300 border-0 text-[10px] font-mono">
                  {count}
                </Badge>
              </NavLink>
            );
          })}

          <div className="pt-4 pb-1 px-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            Yönetim
          </div>

          <NavLink
            to="/dashboard/team"
            data-testid="nav-team"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                  : "text-slate-300 hover:bg-slate-800/60 border border-transparent"
              }`
            }
          >
            <Users className="w-4 h-4" /> Ekip
          </NavLink>

          <NavLink
            to="/dashboard/audit"
            data-testid="nav-audit"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-cyan-500/10 text-cyan-400 border border-cyan-500/20"
                  : "text-slate-300 hover:bg-slate-800/60 border border-transparent"
              }`
            }
          >
            <History className="w-4 h-4" /> Denetim Kaydı
          </NavLink>
        </nav>

        <div className="p-3 border-t border-slate-800">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="user-menu-trigger"
                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/60 transition-colors text-left"
              >
                <Avatar className="w-8 h-8 border border-slate-700">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-emerald-500/20 text-emerald-300 text-xs font-mono">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-slate-100 truncate">{user?.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-400">
                    {user?.role}
                  </div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#111726] border-slate-800 text-slate-100">
              <DropdownMenuLabel className="font-mono text-xs text-slate-400">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-slate-800" />
              <DropdownMenuItem
                data-testid="logout-button"
                onClick={handleLogout}
                className="cursor-pointer focus:bg-rose-500/10 focus:text-rose-400"
              >
                <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-16 flex-shrink-0 flex items-center justify-between px-4 sm:px-6 lg:px-8 border-b border-slate-800 bg-[#0d121f]/80 backdrop-blur-md sticky top-0 z-20">
          <div className="lg:hidden flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-emerald-400" />
            <span className="font-display text-base font-semibold">VaultOps</span>
          </div>
          <div className="flex-1 flex justify-end items-center gap-3">
            <Button
              data-testid="import-button"
              variant="outline"
              onClick={() => setImportOpen(true)}
              className="h-10 border-slate-700 bg-transparent text-slate-200 hover:bg-slate-800 hover:text-slate-100 rounded-lg"
            >
              <Upload className="w-4 h-4 mr-1.5" /> İçe Aktar
            </Button>
            <Button
              data-testid="add-secret-button"
              onClick={() => window.dispatchEvent(new CustomEvent("vaultops:open-create"))}
              className="h-10 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-lg"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Yeni Kayıt
            </Button>
          </div>
        </header>

        <main className="flex-1 min-w-0 overflow-y-auto">
          <Outlet />
        </main>
        <ImportModal open={importOpen} onOpenChange={setImportOpen} />
      </div>
    </div>
  );
}
